import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  Order,
  OrderBy,
  OutreachContactsContext,
} from "contexts/OutreachContacts";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import moment from "moment";
import { KeyboardEvent, useContext, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { Route, Routes, useNavigate } from "react-router-dom";
import { OutreachContact } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import OpenedColumn from "components/OpenedColumn";
import { generateInitials } from "utils/string";

import OutreachContactDrawer from "../../OutreachContactDrawer";
import InboxFilters from "../SearchBar/Filters/InboxFilters";

const REF_INDEX = 5;

const SORTABLE_COLUMNS: {
  label: string;
  orderBy: OrderBy;
  width: string | number;
}[] = [
  { label: "Brand", orderBy: OrderBy.brand_name, width: "18%" },
  { label: "Contact", orderBy: OrderBy.contact, width: "18%" },
  { label: "Opens", orderBy: OrderBy.last_email_open_count, width: "7%" },
  {
    label: "Last Emailed",
    orderBy: OrderBy.last_gmail_message_date,
    width: "13%",
  },
];

const NON_SORTABLE_COLUMNS: { label: string; width: string | number }[] = [
  { label: "Template", width: "12%" },
  { label: "Category", width: "17%" },
  { label: "", width: "15%" },
];

const SORT_OPTIONS: {
  label: string;
  shortLabel: string;
  orderBy: OrderBy;
  order: Order;
}[] = [
  {
    label: "Last Emailed (Newest)",
    shortLabel: "Emailed \u2193",
    orderBy: OrderBy.last_gmail_message_date,
    order: Order.desc,
  },
  {
    label: "Last Emailed (Oldest)",
    shortLabel: "Emailed \u2191",
    orderBy: OrderBy.last_gmail_message_date,
    order: Order.asc,
  },
  {
    label: "Brand (A-Z)",
    shortLabel: "Brand \u2191",
    orderBy: OrderBy.brand_name,
    order: Order.asc,
  },
  {
    label: "Brand (Z-A)",
    shortLabel: "Brand \u2193",
    orderBy: OrderBy.brand_name,
    order: Order.desc,
  },
  {
    label: "Most Opens",
    shortLabel: "Opens \u2193",
    orderBy: OrderBy.last_email_open_count,
    order: Order.desc,
  },
  {
    label: "Least Opens",
    shortLabel: "Opens \u2191",
    orderBy: OrderBy.last_email_open_count,
    order: Order.asc,
  },
  {
    label: "Contact (A-Z)",
    shortLabel: "Contact \u2191",
    orderBy: OrderBy.contact,
    order: Order.asc,
  },
  {
    label: "Contact (Z-A)",
    shortLabel: "Contact \u2193",
    orderBy: OrderBy.contact,
    order: Order.desc,
  },
];

const BrandTracker = () => {
  const {
    contacts,
    fetchLoading,
    fetchMoreLoading,
    fetchContactsParams,
    handleFetchContacts,
    total,
  } = useContext(OutreachContactsContext);
  const { outreachTemplates, setEditTemplateDialogId } = useContext(
    OutreachTemplatesContext,
  );
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  useEffect(() => {
    if (isPaginationRefVisible) {
      if (
        total !== null &&
        contacts.length < total &&
        !fetchMoreLoading &&
        !fetchLoading
      ) {
        handleFetchContacts({
          ...fetchContactsParams,
          page: fetchContactsParams.page + 1,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationRefVisible]);

  const handleSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      const value = (e.target as HTMLInputElement).value;
      handleFetchContacts({
        ...fetchContactsParams,
        page: 1,
        searchQuery: value || undefined,
        selectedSuggestion: null,
      });
    }
  };

  const handleClearSearch = () => {
    handleFetchContacts({
      ...fetchContactsParams,
      page: 1,
      searchQuery: undefined,
      selectedSuggestion: null,
    });
  };

  const sortValue = useMemo(() => {
    return (
      SORT_OPTIONS.find(
        (opt) =>
          opt.order === fetchContactsParams.order &&
          opt.orderBy === fetchContactsParams.orderBy,
      ) || SORT_OPTIONS[0]
    );
  }, [fetchContactsParams.order, fetchContactsParams.orderBy]);

  const handleSortSelect = (e: CustomEvent) => {
    const option =
      SORT_OPTIONS.find((opt) => opt.label === e.target.value) ||
      SORT_OPTIONS[0];
    handleFetchContacts({
      ...fetchContactsParams,
      page: 1,
      orderBy: option.orderBy,
      order: option.order,
    });
  };

  const handleSort = (orderBy: OrderBy) => {
    let newOrder: Order;
    if (fetchContactsParams.orderBy === orderBy) {
      newOrder =
        fetchContactsParams.order === Order.asc ? Order.desc : Order.asc;
    } else {
      newOrder = Order.desc;
    }
    handleFetchContacts({
      ...fetchContactsParams,
      page: 1,
      orderBy,
      order: newOrder,
    });
  };

  const getSortDirection = (orderBy: OrderBy): "asc" | "desc" | undefined => {
    if (fetchContactsParams.orderBy !== orderBy) return undefined;
    return fetchContactsParams.order;
  };

  const getLastEmailedDate = (contact: OutreachContact) => {
    if (!contact.lastGmailMessageDate) return null;
    const date = moment.unix(contact.lastGmailMessageDate);
    return {
      formatted: date.format("MMM D, YYYY"),
      relative: date.fromNow(),
    };
  };

  const getCategoryChips = (contact: OutreachContact) => {
    const categories = contact.bentoBrand?.mainCategories;
    if (!categories || categories.length === 0) return null;
    return categories.map((cat: { id: number; name: string }) => (
      <Chip
        size="small"
        key={cat.id}
        label={cat.name}
        sx={{
          borderRadius: 2,
          fontSize: 10,
        }}
      />
    ));
  };

  const openContact = (contact: OutreachContact) => {
    const url = contact.lastGmailThreadId
      ? `${contact.id}/email/${contact.lastGmailThreadId}`
      : `${contact.id}/email`;
    navigate(url);
  };

  const openContactDraft = (e: React.MouseEvent, contact: OutreachContact) => {
    e.stopPropagation();
    navigate(
      `${contact.id}/email/${contact.lastGmailThreadId}?display_followup_templates=true`,
    );
  };

  const getTemplateName = (contact: OutreachContact) => {
    if (!contact.latestOutreachTemplateId) return null;
    const template = outreachTemplates.find(
      (t) => t.id === contact.latestOutreachTemplateId,
    );
    return template?.name || null;
  };

  const skeletonRows = (
    <>
      {Array.from(new Array(8)).map((_, i) => (
        <TableRow key={`skeleton-${i}`}>
          {Array.from(new Array(7)).map((__, j) => (
            <TableCell key={j}>
              <Skeleton animation="wave" width="80%" height={24} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  const mobileSkeletons = (
    <>
      {Array.from(new Array(6)).map((_, i) => (
        <Box
          key={`skeleton-${i}`}
          sx={{
            p: 2,
            mx: 1,
            mb: 1,
            borderRadius: 2,
            backgroundColor: "white",
            border: `1px solid ${grey[200]}`,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
            <Skeleton variant="circular" width={48} height={48} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" height={22} />
              <Skeleton width="80%" height={18} />
              <Skeleton width="50%" height={18} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
      ))}
    </>
  );

  const renderMobileCard = (contact: OutreachContact, index: number) => {
    const lastEmailed = getLastEmailedDate(contact);
    const templateName = getTemplateName(contact);

    return (
      <Box
        key={contact.id}
        ref={
          (
            contacts.length > REF_INDEX
              ? index === contacts.length - REF_INDEX
              : index === contacts.length - 1
          )
            ? paginationRef
            : undefined
        }
        onClick={() => openContact(contact)}
        sx={{
          p: 2,
          mx: 1,
          mb: 1,
          borderRadius: 2,
          backgroundColor: "white",
          border: `1px solid ${grey[200]}`,
          cursor: "pointer",
          "&:hover": { backgroundColor: grey[50] },
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <Avatar
            src={contact.bentoBrand?.logoUrl}
            imgProps={{
              onError: (e) => {
                (e.target as HTMLImageElement).style.display = "none";
              },
            }}
            sx={{
              width: 48,
              height: 48,
              fontSize: 18,
              fontWeight: 600,
              bgcolor: "#D0FAE5",
              color: "#007A55",
              flexShrink: 0,
            }}
          >
            {generateInitials(contact.brand?.name || "")}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600} noWrap>
              {contact.brand?.name || "—"}
            </Typography>
            <Typography variant="body2" sx={{ color: grey[600] }} noWrap>
              {contact.email || "—"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.5,
                flexWrap: "wrap",
              }}
            >
              <OpenedColumn contact={contact} compact />
              {lastEmailed && (
                <Typography
                  variant="caption"
                  sx={{
                    color: grey[500],
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Box
                    component="i"
                    className="fa-regular fa-clock"
                    sx={{ fontSize: 11 }}
                  />
                  {lastEmailed.relative}
                </Typography>
              )}
              {getCategoryChips(contact)}
              {templateName && (
                <Typography
                  variant="caption"
                  component="span"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (contact.latestOutreachTemplateId) {
                      setEditTemplateDialogId(
                        String(contact.latestOutreachTemplateId),
                      );
                    }
                  }}
                  sx={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    "&:hover": { color: "text.primary" },
                  }}
                >
                  {templateName}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        {contact.email && contact.lastGmailThreadId && (
          <Button
            fullWidth
            size="small"
            variant="outlined"
            disableElevation
            sx={{
              mt: 1.5,
              textTransform: "none",
              fontSize: 13,
              borderColor: grey[300],
              color: "text.primary",
              "&:hover": { borderColor: grey[400] },
            }}
            startIcon={
              <Box
                component="i"
                className="fa-regular fa-paper-plane"
                sx={{ fontSize: "12px !important" }}
              />
            }
            onClick={(e) => openContactDraft(e, contact)}
          >
            Send Follow Up
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        overflow: "auto",
        height: "calc(100vh - 100px - var(--inbox-alerts-h, 0px))",
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h5" gutterBottom>
          Brand Tracker
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track your outreach and identify brands to follow up with
        </Typography>
      </Box>
      <Grid container alignItems="center" gap={2} sx={{ px: 1, py: 1.5 }}>
        <Grid item xs sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            fullWidth
            key={fetchContactsParams.searchQuery ?? ""}
            defaultValue={fetchContactsParams.searchQuery ?? ""}
            variant="outlined"
            size="small"
            placeholder="Search brands, contacts..."
            onKeyDown={handleSearchKeyDown}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F9FAFB",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="i" className="fa-regular fa-search" />
                </InputAdornment>
              ),
              endAdornment: fetchContactsParams.searchQuery ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <Box component="i" className="fa-regular fa-xmark" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <InboxFilters />
        </Grid>
        <Grid item xs="auto">
          <FormControl variant="outlined" size="small">
            <Select
              sx={{
                backgroundColor: "white",
                height: 35,
                borderRadius: 4,
                fontSize: 13,
              }}
              onChange={handleSortSelect}
              value={sortValue.label}
              renderValue={(selected) => {
                const opt = SORT_OPTIONS.find((o) => o.label === selected);
                return (
                  <>
                    <Box
                      sx={{ mr: 1 }}
                      component="i"
                      className="fa-solid fa-arrow-up-arrow-down"
                    />
                    {isMobile ? opt?.shortLabel || selected : selected}
                  </>
                );
              }}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.label}
                  value={opt.label}
                  sx={{ fontSize: 13 }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {isMobile ? (
        <Box sx={{ pt: 1 }}>
          {fetchLoading && mobileSkeletons}
          {!fetchLoading &&
            contacts.map((contact, index) => renderMobileCard(contact, index))}
        </Box>
      ) : (
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1, py: 0.75 },
          }}
        >
          <TableHead>
            <TableRow>
              {SORTABLE_COLUMNS.map((col) => {
                const direction = getSortDirection(col.orderBy);
                return (
                  <TableCell
                    key={col.orderBy}
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                      textTransform: "uppercase",
                      color: "#4A5565",
                      letterSpacing: 0.5,
                      backgroundColor: "#F9FAFB",
                      width: col.width,
                    }}
                    sortDirection={direction || false}
                  >
                    <TableSortLabel
                      active={!!direction}
                      direction={direction || "desc"}
                      onClick={() => handleSort(col.orderBy)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
              {NON_SORTABLE_COLUMNS.map((col) => (
                <TableCell
                  key={col.label}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: "uppercase",
                    color: "#4A5565",
                    letterSpacing: 0.5,
                    backgroundColor: "#F9FAFB",
                    width: col.width,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchLoading && skeletonRows}
            {!fetchLoading &&
              contacts.map((contact, index) => (
                <TableRow
                  key={contact.id}
                  ref={
                    (
                      contacts.length > REF_INDEX
                        ? index === contacts.length - REF_INDEX
                        : index === contacts.length - 1
                    )
                      ? paginationRef
                      : undefined
                  }
                  hover
                  onClick={() => openContact(contact)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell sx={{ overflow: "hidden" }}>
                    <Grid container alignItems="center" wrap="nowrap">
                      <Avatar
                        src={contact.bentoBrand?.logoUrl}
                        imgProps={{
                          onError: (e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          },
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          mr: 1,
                          fontSize: 13,
                          fontWeight: 600,
                          bgcolor: "#D0FAE5",
                          color: "#007A55",
                          flexShrink: 0,
                        }}
                      >
                        {generateInitials(contact.brand?.name || "")}
                      </Avatar>
                      <Typography variant="body2" noWrap fontWeight={600}>
                        {contact.brand?.name || "—"}
                      </Typography>
                    </Grid>
                  </TableCell>
                  <TableCell sx={{ overflow: "hidden" }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ color: grey[600] }}
                    >
                      {contact.email || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <OpenedColumn contact={contact} compact />
                  </TableCell>
                  <TableCell>
                    {getLastEmailedDate(contact) ? (
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {getLastEmailedDate(contact)!.formatted}
                        </Typography>
                        <Typography variant="caption" sx={{ color: grey[500] }}>
                          {getLastEmailedDate(contact)!.relative}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ overflow: "hidden" }}>
                    {getTemplateName(contact) ? (
                      <Button
                        variant="text"
                        color="inherit"
                        size="small"
                        sx={{
                          textTransform: "none",
                          textDecoration: "underline",
                          fontSize: 12,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                          textAlign: "left",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (contact.latestOutreachTemplateId) {
                            setEditTemplateDialogId(
                              String(contact.latestOutreachTemplateId),
                            );
                          }
                        }}
                      >
                        {getTemplateName(contact)}
                      </Button>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: grey[400], fontSize: 11 }}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {getCategoryChips(contact) || (
                        <Typography
                          variant="body2"
                          sx={{ color: grey[400], fontSize: 11 }}
                        >
                          —
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {contact.email && contact.lastGmailThreadId ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disableElevation
                        sx={{
                          textTransform: "none",
                          fontSize: 12,
                          borderColor: grey[300],
                          color: "text.primary",
                          whiteSpace: "nowrap",
                          "&:hover": { borderColor: grey[400] },
                        }}
                        startIcon={
                          <Box
                            component="i"
                            className="fa-regular fa-paper-plane"
                            sx={{ fontSize: "12px !important" }}
                          />
                        }
                        onClick={(e) => openContactDraft(e, contact)}
                      >
                        Follow Up
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}
      {fetchMoreLoading && (
        <Box display="flex" justifyContent="center" sx={{ py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Loading More Rows
          </Typography>
        </Box>
      )}

      <Routes>
        <Route
          path=":outreachContactId/*"
          element={<OutreachContactDrawer />}
        />
      </Routes>
    </Box>
  );
};

export default BrandTracker;
